import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { IMPORTANT_TIMEZONES, formatTimezoneLabel, getDefaultTimezone } from '../constants/timezones';
import '../styles/design-system.css';
import '../styles/Dashboard.css';

function BrandDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [brand, setBrand] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [scheduleCount, setScheduleCount] = useState(1);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleTimezone, setScheduleTimezone] = useState(getDefaultTimezone());
  const [schedulingGenerate, setSchedulingGenerate] = useState(false);
  const [showPreviousPosts, setShowPreviousPosts] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [deletingPost, setDeletingPost] = useState(false);

  const getTodayLocalDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const resolveMediaUrl = (path) => {
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const apiBase = (api.defaults.baseURL || window.location.origin).replace(/\/$/, '');
    return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
  };

  useEffect(() => { fetchBrandData(); }, [id]);

  useEffect(() => {
    setScheduleDate(getTodayLocalDate());
  }, []);

  useEffect(() => {
    if (posts.length > 0) {
      const selected = selectedPostId ? posts.find((p) => p.id === selectedPostId) : null;
      const fallbackPost = selected || posts[0];
      setCaptionDraft(fallbackPost?.caption || '');
      if (!selectedPostId || !selected) {
        setSelectedPostId(posts[0].id);
      }
    }
  }, [posts, selectedPostId]);

  const fetchBrandData = async () => {
    try {
      const [brandRes, postsRes] = await Promise.all([
        api.get(`/api/brands/${id}`),
        api.get(`/api/brands/${id}/posts`, { params: { limit: 100 } }),
      ]);
      const brandData = brandRes.data;
      setBrand(brandData);
      setPosts(postsRes.data);
      setScheduleTime(brandData.auto_post_time || '09:00');
      setScheduleTimezone(brandData.auto_post_timezone || getDefaultTimezone());

    } catch (error) {
      console.error('Failed to fetch brand data:', error);
      alert('Failed to load brand data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post(`/brand/${id}/generate`);
      alert('Post generated successfully!');
      fetchBrandData();
    } catch (error) {
      alert('Failed to generate post: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally { setGenerating(false); }
  };

  const handleGenerateScheduled = async () => {
    const count = Math.max(1, Math.min(7, Number(scheduleCount) || 1));
    if (!scheduleDate || !scheduleTime) {
      alert('Please select both date and time');
      return;
    }

    setSchedulingGenerate(true);
    try {
      const response = await api.post(`/brand/${id}/generate`, {
        posts_count: count,
        schedule_start_date: scheduleDate,
        schedule_time: scheduleTime,
        schedule_timezone: scheduleTimezone,
      });
      alert(response.data?.message || 'Posts generated and scheduled successfully!');
      fetchBrandData();
    } catch (error) {
      alert('Failed to generate scheduled posts: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSchedulingGenerate(false);
    }
  };

  const handlePublishNow = async () => {
    if (!window.confirm('Publish the latest post now to your connected social accounts?')) return;
    setPublishing(true);
    try {
      await api.post(`/brand/${id}/publish-now`);
      alert('Post published successfully!');
      fetchBrandData();
    } catch (error) {
      alert('Failed to publish post: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally { setPublishing(false); }
  };



  const handleDelete = async () => {
    if (!window.confirm('Delete this brand and all its posts? This cannot be undone.')) return;
    try {
      await api.post(`/brand/${id}/delete`);
      navigate('/dashboard');
    } catch (error) {
      alert('Failed to delete brand: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleSaveCaption = async () => {
    const activePost = posts.find((p) => p.id === selectedPostId) || (posts.length > 0 ? posts[0] : null);
    if (!activePost) return;

    if (!captionDraft.trim()) {
      alert('Caption cannot be empty');
      return;
    }

    setSavingCaption(true);
    try {
      await api.post(`/brand/${id}/posts/${activePost.id}/caption`, { caption: captionDraft.trim() });
      setEditingCaption(false);
      await fetchBrandData();
      alert('Caption updated successfully!');
    } catch (error) {
      alert('Failed to update caption: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingCaption(false);
    }
  };

  const handleDeleteOpenedPost = async () => {
    const activePost = posts.find((p) => p.id === selectedPostId) || (posts.length > 0 ? posts[0] : null);
    if (!activePost) return;

    if (!window.confirm('Delete this post? This action cannot be undone.')) return;

    setDeletingPost(true);
    try {
      await api.post(`/brand/${id}/posts/${activePost.id}/delete`);
      setEditingCaption(false);
      await fetchBrandData();
      alert('Post deleted successfully!');
    } catch (error) {
      alert('Failed to delete post: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setDeletingPost(false);
    }
  };



  if (loading) {
    return (
      <div className="ps-loading">
        <div className="ps-spinner" />
        <span className="ps-loading-text">Loading brand…</span>
      </div>
    );
  }

  const latestPost = posts.length > 0 ? posts[0] : null;
  const activePost = posts.find((p) => p.id === selectedPostId) || latestPost;
  const olderPosts = posts.slice(1);
  const now = new Date();
  const upcomingScheduledPosts = posts
    .filter((post) => post.status === 'scheduled' && post.scheduled_for)
    .map((post) => ({ ...post, scheduledDate: new Date(post.scheduled_for) }))
    .filter((post) => !Number.isNaN(post.scheduledDate.getTime()) && post.scheduledDate >= now)
    .sort((a, b) => a.scheduledDate - b.scheduledDate);
  const nextScheduledPost = upcomingScheduledPosts.length > 0 ? upcomingScheduledPosts[0] : null;
  const timezoneOptions = scheduleTimezone && !IMPORTANT_TIMEZONES.includes(scheduleTimezone)
    ? [scheduleTimezone, ...IMPORTANT_TIMEZONES]
    : IMPORTANT_TIMEZONES;

  return (
    <div className="ps-page">
      {/* ── Navbar ── */}
      <nav className="ps-nav">
        <div className="ps-container">
          <div className="ps-nav__inner">
            <Link to="/dashboard" className="ps-nav__logo">
              Postrify <span className="ps-nav__logo-dot" />
            </Link>
            <div className="ps-nav__right">
              <Link to={`/brand/${id}/settings`} className="ps-btn ps-btn--secondary ps-btn--sm">⚙ Settings</Link>
              <Link to="/dashboard" className="ps-btn ps-btn--ghost ps-btn--sm">← Dashboard</Link>
              <button onClick={logout} className="ps-btn ps-btn--secondary ps-btn--sm">Log out</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="ps-container">
        {/* ── Page Header ── */}
        <div className="ps-page-header">
          <div>
            <h1 className="ps-page-header__title">{brand.name}</h1>
            <p className="ps-page-header__sub">
              <span className="ps-badge ps-badge--primary" style={{marginRight:8}}>{brand.niche}</span>
              <span className="ps-badge ps-badge--muted">{brand.tone}</span>
            </p>
          </div>
          <div className="ps-page-header__actions">
            {/* Admin tasks on left */}
            <Link to={`/brand/${id}/settings`} className="ps-btn ps-btn--secondary ps-btn--sm">⚙ Settings</Link>
            <Link to={`/brand/${id}/edit`} className="ps-btn ps-btn--secondary ps-btn--sm">Edit Brand</Link>
            {/* High-value actions on right */}
            <button onClick={handleGenerate} className="ps-btn ps-btn--primary"
              disabled={generating}>
              {generating ? 'Generating…' : '⚡ Generate Post'}
            </button>
            <button onClick={handlePublishNow} className="ps-btn ps-btn--success ps-btn--sm"
              disabled={publishing || !latestPost}>
              {publishing ? 'Publishing…' : '📤 Publish Now'}
            </button>
            <button
              onClick={() => setShowPreviousPosts((prev) => !prev)}
              className="ps-btn ps-btn--secondary ps-btn--sm"
              disabled={!olderPosts.length}
            >
              {showPreviousPosts ? 'Hide Previous Posts' : 'View Previous Posts'}
            </button>
          </div>
        </div>

        <div className="ps-card" style={{ marginBottom: '16px' }}>
          <div className="ps-card__title"><span className="ps-card__title-icon">📅</span> Generate & Schedule (Max 7 days)</div>
          <p className="ps-page-header__sub" style={{ marginBottom: '12px' }}>
            Note: Generated posts are automatically deleted after 7 days.
          </p>
          {nextScheduledPost && (
            <p className="ps-page-header__sub" style={{ marginBottom: '12px' }}>
              Next scheduled publish at: {nextScheduledPost.scheduledDate.toLocaleString()}
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'end' }}>
            <div>
              <label className="ps-label">Number of posts</label>
              <input
                type="number"
                min="1"
                max="7"
                value={scheduleCount}
                onChange={(e) => setScheduleCount(e.target.value)}
                className="ps-input"
              />
            </div>
            <div>
              <label className="ps-label">Start date</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="ps-input"
              />
            </div>
            <div>
              <label className="ps-label">Time</label>
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="ps-input"
              />
            </div>
            <div>
              <label className="ps-label">Timezone</label>
              <select
                className="ps-input"
                value={scheduleTimezone}
                onChange={(e) => setScheduleTimezone(e.target.value)}
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>{formatTimezoneLabel(tz)}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="ps-btn ps-btn--primary"
              onClick={handleGenerateScheduled}
              disabled={schedulingGenerate}
            >
              {schedulingGenerate ? 'Scheduling…' : 'Generate & Schedule'}
            </button>
          </div>
        </div>



        {/* ── Latest Post ── */}
        {activePost ? (
          <>
            <div className="ps-card">
              <div className="ps-card__title">
                <span className="ps-card__title-icon">✨</span> {activePost.id === latestPost?.id ? 'Latest Post' : 'Opened Post'}
              </div>
              <div className="post-hero">
                {brand.logo_path && (
                  <img src={resolveMediaUrl(brand.logo_path)} alt="Brand logo" className="post-hero__brand-logo" />
                )}
                <div className="post-hero__meta">
                  🕐 {new Date(activePost.created_at).toLocaleString()}
                  {activePost.scheduled_for ? ` · 📅 Scheduled: ${new Date(activePost.scheduled_for).toLocaleString()}` : ''}
                </div>
                <h3 className="post-hero__topic">{activePost.topic}</h3>
                {editingCaption ? (
                  <div style={{ marginBottom: '14px' }}>
                    <label className="ps-label" style={{ marginBottom: '8px' }}>Edit Caption</label>
                    <textarea
                      className="ps-input"
                      value={captionDraft}
                      onChange={(e) => setCaptionDraft(e.target.value)}
                      rows={6}
                      style={{ resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={handleSaveCaption}
                        className="ps-btn ps-btn--primary ps-btn--sm"
                        disabled={savingCaption}
                      >
                        {savingCaption ? 'Saving…' : 'Save Caption'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCaption(false);
                          setCaptionDraft(activePost.caption || '');
                        }}
                        className="ps-btn ps-btn--secondary ps-btn--sm"
                        disabled={savingCaption}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="post-hero__caption">{activePost.caption}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setCaptionDraft(activePost.caption || '');
                        setEditingCaption(true);
                      }}
                      className="ps-btn ps-btn--secondary ps-btn--sm"
                      style={{ marginBottom: '14px' }}
                    >
                      Edit Caption
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteOpenedPost}
                      className="ps-btn ps-btn--danger ps-btn--sm"
                      style={{ marginBottom: '14px', marginLeft: '8px' }}
                      disabled={deletingPost}
                    >
                      {deletingPost ? 'Deleting…' : 'Delete Post'}
                    </button>
                  </>
                )}
                <div className="post-hero__cta">📢 {activePost.cta}</div>
                <div className="post-hero__hashtags">{activePost.hashtags}</div>
              </div>
              {activePost.images?.length > 0 && (
                <div className="post-images">
                  {activePost.images.map((img, idx) => (
                    <div key={idx} className="post-image-card">
                      <img src={resolveMediaUrl(img.file_path)} alt={`Generated image ${idx + 1}`} />
                      {(img.image_prompt || img.prompt) && (
                        <div className="post-image-card__caption">{img.image_prompt || img.prompt}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {olderPosts.length > 0 && showPreviousPosts && (
              <div className="ps-card">
                <div className="ps-card__title"><span className="ps-card__title-icon">🗂</span> Post History</div>
                <div className="post-history-list">
                  {olderPosts.map((post) => (
                    <div
                      key={post.id}
                      className="post-history-item"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedPostId(post.id);
                        setEditingCaption(false);
                        setCaptionDraft(post.caption || '');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedPostId(post.id);
                          setEditingCaption(false);
                          setCaptionDraft(post.caption || '');
                        }
                      }}
                      style={{ cursor: 'pointer', border: selectedPostId === post.id ? '1px solid rgba(72, 99, 255, 0.35)' : undefined }}
                    >
                      <div className="post-history-item__header">
                        <div>
                          <div className="post-history-item__topic">{post.topic}</div>
                          <div className="post-history-item__date">{new Date(post.created_at).toLocaleString()}</div>
                          {post.scheduled_for && (
                            <div className="post-history-item__date">Scheduled: {new Date(post.scheduled_for).toLocaleString()}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {selectedPostId === post.id && <span className="ps-badge ps-badge--primary">Opened</span>}
                          <span className="ps-badge ps-badge--accent">{post.status}</span>
                        </div>
                      </div>
                      <p className="post-history-item__excerpt">
                        {post.caption.length > 160 ? post.caption.substring(0, 160) + '…' : post.caption}
                      </p>
                      <div className="post-history-item__footer">
                        <span>🖼 {post.images?.length || 0} image{post.images?.length !== 1 ? 's' : ''}</span>
                        <span># {post.hashtags?.split(' ').length || 0} hashtags</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="ps-empty">
            <div className="ps-empty__icon">📝</div>
            <h2 className="ps-empty__title">No posts yet</h2>
            <p className="ps-empty__desc">Generate your first AI-powered post to get started.</p>
            <button onClick={handleGenerate} className="ps-btn ps-btn--primary ps-btn--lg" disabled={generating}>
              {generating ? 'Generating…' : '⚡ Generate first post'}
            </button>
          </div>
        )}

        {/* ── Danger Zone ── */}
        <div className="ps-card danger-zone" style={{marginBottom:'40px'}}>
          <div className="ps-card__title" style={{color:'#991b1b'}}>⚠ Danger Zone</div>
          <p style={{fontSize:'14px',color:'#4b4a6b',marginBottom:'16px'}}>
            Permanently delete this brand and all its posts. This action cannot be undone.
          </p>
          <button onClick={handleDelete} className="ps-btn ps-btn--danger ps-btn--sm">Delete Brand</button>
        </div>
      </div>

      <footer className="ps-footer">
        <div className="ps-container">
          © 2024 Postrify · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

export default BrandDetail;
